import React from 'react';
import { Box, Grid, Paper, Typography, Divider, Chip, IconButton, CircularProgress, Dialog, DialogTitle, DialogContent, Button, Autocomplete, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, LabelList } from 'recharts';
import PageLayout from '../components/common/PageLayout';
import { Link, useLocation } from 'react-router-dom';
import { get } from '../utils/api';
import { fetchPledgesByProduct } from '../utils/pledgeReports';
import { PendingPledgesDialog } from '../components/dashboard/PendingPledgesDialog';
import { DonationsByCustomerDialog } from '../components/dashboard/DonationsByCustomerDialog';
import { OrdersDialog } from '../components/dashboard/DashboardTempleDialogs';
import { getKotilingaCategorySlugsCsv } from '../utils/kotilingaScope';

const Kpi = ({ label, value }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: '#eef2f7',
      border: '1px solid',
      borderColor: '#e2e8f0',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
    }}
  >
    <Typography variant="overline" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 700 }}>{value ?? '--'}</Typography>
  </Paper>
);

function formatAmount(n) {
  const v = Number(n);
  if (!isFinite(v)) return '--';
  return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function DashboardTemplePage() {
  const location = useLocation();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [summary, setSummary] = React.useState(null);
  const [pledges, setPledges] = React.useState(null);
  const [recent, setRecent] = React.useState([]);
  const [seriesMonth, setSeriesMonth] = React.useState([]);
  const [seriesYear, setSeriesYear] = React.useState([]);
  const [prodSplit, setProdSplit] = React.useState([]);
  const [catSplit, setCatSplit] = React.useState([]);
  const [pledgesTop, setPledgesTop] = React.useState([]);
  const [pledgesRaw, setPledgesRaw] = React.useState([]);
  const [pledgesByProduct, setPledgesByProduct] = React.useState([]);
  const [pledgesPage, setPledgesPage] = React.useState(1);
  const [pledgesPerPage, setPledgesPerPage] = React.useState(10);
  const [pledgeDlgOpen, setPledgeDlgOpen] = React.useState(false);
  const [pledgeDlgData, setPledgeDlgData] = React.useState({ data: [], total: 0, page: 1, per_page: 25 });
  // Pledges product expand state
  const [pledgeExpanded, setPledgeExpanded] = React.useState({}); // product_id -> bool
  const [pledgeVarMap, setPledgeVarMap] = React.useState({}); // product_id -> [variation rows]
  // Orders-by-product dialog state
  const [orderDlgOpen, setOrderDlgOpen] = React.useState(false);
  const [orderDlgProduct, setOrderDlgProduct] = React.useState(null);
  const prodPieRef = React.useRef(null);
  const catPieRef = React.useRef(null);
  const [prodW, setProdW] = React.useState(0);
  const [catW, setCatW] = React.useState(0);
  const prodBarRef = React.useRef(null);
  const [prodBarW, setProdBarW] = React.useState(0);
  // Timeline controls
  const [rangeType, setRangeType] = React.useState('last12'); // all | last12 | lastYear | ytd | custom | year
  const [rangeYear, setRangeYear] = React.useState(new Date().getFullYear());
  const [customStart, setCustomStart] = React.useState(''); // yyyy-mm-dd
  const [customEnd, setCustomEnd] = React.useState('');
  const [expanded, setExpanded] = React.useState({});
  const [variationsMap, setVariationsMap] = React.useState({});
  const [prodPage, setProdPage] = React.useState(1);
  const [prodPerPage, setProdPerPage] = React.useState(10);
  const [selectedProductFilter, setSelectedProductFilter] = React.useState(null); // For Top 10 Products table
  const [selectedPledgeProductFilter, setSelectedPledgeProductFilter] = React.useState(null); // For Pending Pledges table
  
  const RADIAN = Math.PI / 180;
  const renderPieLabel = (props) => {
    const { cx, cy, midAngle, outerRadius, name, value } = props;
    const radius = outerRadius + 14;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#424242" fontSize={11} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${name}: ${formatAmount(value)}`}
      </text>
    );
  };

  const buildRangeParams = () => {
    try {
      const now = new Date();
      let start = null, end = null;
      if (rangeType === 'last12') {
        end = now;
        start = new Date(now);
        start.setMonth(start.getMonth() - 12);
      } else if (rangeType === 'lastYear') {
        const y = now.getFullYear() - 1;
        start = new Date(Date.UTC(y, 0, 1));
        end = new Date(Date.UTC(y, 11, 31, 23, 59, 59, 999));
      } else if (rangeType === 'ytd') {
        const y = now.getFullYear();
        start = new Date(Date.UTC(y, 0, 1));
        end = now;
      } else if (rangeType === 'year') {
        const y = Number(rangeYear || now.getFullYear());
        start = new Date(Date.UTC(y, 0, 1));
        end = new Date(Date.UTC(y, 11, 31, 23, 59, 59, 999));
      } else if (rangeType === 'custom') {
        start = customStart ? new Date(`${customStart}T00:00:00.000Z`) : null;
        end = customEnd ? new Date(`${customEnd}T23:59:59.999Z`) : null;
      }
      const parts = [];
      if (start && !isNaN(start)) parts.push(`start=${encodeURIComponent(start.toISOString())}`);
      if (end && !isNaN(end)) parts.push(`end=${encodeURIComponent(end.toISOString())}`);
      return parts.length ? `&${parts.join('&')}` : '';
    } catch {
      return '';
    }
  };

  const buildProdRangeParams = buildRangeParams;

  // Load product split data with independent date range
  React.useEffect(() => {
    (async () => {
      try {
        const exclude = encodeURIComponent(getKotilingaCategorySlugsCsv());
        const prodRange = buildProdRangeParams();
        const ps = await get(`/ldb/stats/product-split?limit=10&status=completed&exclude_parent_category_slugs=${exclude}${prodRange}`);
        setProdSplit(Array.isArray(ps?.data) ? ps.data : []);
      } catch (e) {
        console.error('Failed to load product split:', e);
      }
    })();
  }, [rangeType, rangeYear, customStart, customEnd]);

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');
        const exclude = encodeURIComponent(getKotilingaCategorySlugsCsv());
        const range = buildRangeParams();
        const [s, r, m, cs, pl] = await Promise.all([
          get(`/ldb/stats/summary?status=completed&exclude_parent_category_slugs=${exclude}${range}`),
          get(`/ldb/orders/recent?limit=10&exclude_parent_category_slugs=${exclude}&status=completed${range}`),
          get(`/ldb/stats/donations-series?interval=month&status=completed&exclude_parent_category_slugs=${exclude}${range}`),
          get(`/ldb/stats/category-split?limit=10&status=completed&exclude_parent_category_slugs=${exclude}${range}`),
          get(`/ldb/reports/pledges/list?status=all&per_page=100&exclude_parent_category_slugs=${exclude}${range}`)
        ]);
        setSummary(s?.summary || null);
        setRecent(Array.isArray(r?.data) ? r.data : []);
        setSeriesMonth(Array.isArray(m?.series) ? m.series : []);
        setCatSplit(Array.isArray(cs?.data) ? cs.data : []);
        const pledgesList = Array.isArray(pl?.data) ? pl.data : [];
        setPledgesRaw(pledgesList);
        const top10 = [...pledgesList]
          .sort((a, b) => Number(b.pledge_amount || 0) - Number(a.pledge_amount || 0))
          .slice(0, 10);
        setPledgesTop(top10);
        
        // Single source of truth: derive both summary and table from by-product endpoint.
        try {
          const exclude = encodeURIComponent(getKotilingaCategorySlugsCsv());
          const range = buildRangeParams();
          const byProductParams = range.startsWith('&')
            ? Object.fromEntries(new URLSearchParams(range.slice(1)))
            : {};
          const { rows: byProd, summary: pledgeSummary } = await fetchPledgesByProduct(get, {
            status: 'all',
            exclude_parent_category_slugs: decodeURIComponent(exclude),
            ...byProductParams,
          });
          setPledgesByProduct(byProd);
          setPledges(pledgeSummary);
          console.log('[Dashboard] Loaded pledges by product:', byProd.length, 'products');
        } catch (e) {
          console.error('[Dashboard] Failed to load pledges by product:', e);
          setPledgesByProduct([]);
          setPledges({ total_count: 0, total_amount: 0, paid_amount: 0, balance_amount: 0 });
        }
      } catch (e) {
        setError(e?.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, [rangeType, rangeYear, customStart, customEnd]);

  // Load yearly chart data separately - always show all available years
  React.useEffect(() => {
    (async () => {
      try {
        const exclude = encodeURIComponent(getKotilingaCategorySlugsCsv());
        // Fetch yearly data without date range filters to show all available years
        const y = await get(`/ldb/stats/donations-series?interval=year&status=completed&exclude_parent_category_slugs=${exclude}`);
        setSeriesYear(Array.isArray(y?.series) ? y.series : []);
      } catch (e) {
        console.error('Failed to load yearly series:', e);
        setSeriesYear([]);
      }
    })();
  }, []); // Only run once on mount, not dependent on date range filters

  // Reset donations pagination when data set changes
  React.useEffect(() => { setProdPage(1); setExpanded({}); }, [prodSplit]);

  const openOrdersDialog = async (product) => {
    setOrderDlgProduct(product);
    setOrderDlgOpen(true);
  };
  const closeOrdersDialog = () => {
    setOrderDlgOpen(false);
    setOrderDlgProduct(null);
  };

  const toggleExpandProduct = async (product) => {
    const pid = product.product_id;
    const isOpen = !!expanded[pid];
    if (isOpen) {
      setExpanded(prev => ({ ...prev, [pid]: false }));
      return;
    }
    if (Array.isArray(variationsMap[pid]) && variationsMap[pid].length === 0) {
      return;
    }
    if (!variationsMap[pid]) {
      try {
        const exclude = encodeURIComponent(getKotilingaCategorySlugsCsv());
        const prodRange = buildProdRangeParams();
        const res = await get(`/ldb/stats/product-variations?product_id=${encodeURIComponent(pid)}&status=completed&exclude_parent_category_slugs=${exclude}${prodRange}`);
        const rows = Array.isArray(res?.data) ? res.data : [];
        const cleaned = rows.filter(v => Number(v.variation_id || 0) !== 0);
        setVariationsMap(prev => ({ ...prev, [pid]: cleaned }));
        if (cleaned.length === 0) {
          setExpanded(prev => ({ ...prev, [pid]: false }));
          return;
        }
      } catch {}
    }
    setExpanded(prev => ({ ...prev, [pid]: true }));
  };

  // Pledges-by-Devotee dialog for a selected product
  const [pledgeDevDlgOpen, setPledgeDevDlgOpen] = React.useState(false);
  const [pledgeDevDlgProduct, setPledgeDevDlgProduct] = React.useState(null);
  
  const openPledgesByDevoteeDialog = (product, variation = null) => {
    setPledgeDevDlgProduct({ ...product, variation });
    setPledgeDevDlgOpen(true);
  };
  
  const closePledgesByDevoteeDialog = () => {
    setPledgeDevDlgOpen(false);
    setPledgeDevDlgProduct(null);
  };

  // Donations-by-Customer dialog for a selected product/variation
  const [donationsDlgOpen, setDonationsDlgOpen] = React.useState(false);
  const [donationsDlgProduct, setDonationsDlgProduct] = React.useState(null);
  
  const openDonationsByCustomerDialog = (product, variation = null) => {
    setDonationsDlgProduct({ ...product, variation });
    setDonationsDlgOpen(true);
  };
  
  const closeDonationsByCustomerDialog = () => {
    setDonationsDlgOpen(false);
    setDonationsDlgProduct(null);
  };

  const handleDownloadCSV = async () => {
    try {
    const exclude = encodeURIComponent(getKotilingaCategorySlugsCsv());
    const range = buildRangeParams();
      
      // Fetch all products and their variations
      const productsRes = await get(`/ldb/reports/pledges/by-product?exclude_parent_category_slugs=${exclude}${range}`);
      const products = Array.isArray(productsRes?.data) ? productsRes.data : [];
      
      // Collect all customer-level data
      const allCustomerData = [];
      
      for (const product of products) {
        const pid = product.product_id;
        
        // Fetch variations for this product
        const variationsRes = await get(`/ldb/reports/pledges/by-product/${pid}/variations?exclude_parent_category_slugs=${exclude}${range}`);
        const variations = Array.isArray(variationsRes?.data) ? variationsRes.data : [];
        
        for (const variation of variations) {
          const vid = variation.variation_id || 0;
          
          // Fetch customer-level data for this variation
          try {
            const customersRes = await get(`/ldb/reports/pledges/by-product/${pid}/variations/${vid}/customers?exclude_parent_category_slugs=${exclude}${range}`);
            const customers = Array.isArray(customersRes?.data) ? customersRes.data : [];
            
            for (const customer of customers) {
              // Use product_url from product data, or construct fallback link
              const productUrl = product.product_url || '';
              // If product_url exists, use it (user can choose variation after clicking)
              // Otherwise, fallback to constructed link
              const paymentLink = productUrl;
              
              allCustomerData.push({
                customerName: customer.name || 'Unknown',
                phone: customer.billing_phone || '',
                email: customer.email || '',
                productName: product.product_name || `Product #${pid}`,
                variationName: variation.name || (vid ? `Variation #${vid}` : ''),
                productUrl: productUrl,
                paymentLink: paymentLink,
                pledged: Number(customer.pledge_total || 0),
                paid: Number(customer.paid_total || 0),
                balance: Number(customer.balance_total || 0)
              });
      }
    } catch (e) {
            console.error(`[Download] Failed to load customers for product ${pid}, variation ${vid}:`, e);
          }
        }
    }
    
      // Generate CSV
      const headers = ['Customer Name', 'Phone', 'Email', 'Product Name', 'Variation Name', 'Product URL', 'Payment Link', 'Pledged', 'Paid', 'Balance'];
      const csvRows = [
        headers.join(','),
        ...allCustomerData.map(row => [
          `"${row.customerName.replace(/"/g, '""')}"`,
          `"${row.phone.replace(/"/g, '""')}"`,
          `"${row.email.replace(/"/g, '""')}"`,
          `"${row.productName.replace(/"/g, '""')}"`,
          `"${row.variationName.replace(/"/g, '""')}"`,
          `"${row.productUrl.replace(/"/g, '""')}"`,
          `"${row.paymentLink.replace(/"/g, '""')}"`,
          row.pledged.toFixed(2),
          row.paid.toFixed(2),
          row.balance.toFixed(2)
        ].join(','))
      ];
      
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `pending_pledges_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('[Download] Failed to generate CSV:', e);
      alert('Failed to download CSV. Please try again.');
    }
  };

  const toggleExpandPledgeProduct = async (product) => {
    const pid = product.product_id;
    const isOpen = !!pledgeExpanded[pid];
    if (isOpen) { setPledgeExpanded(prev => ({ ...prev, [pid]: false })); return; }
    if (!pledgeVarMap[pid] || pledgeVarMap[pid].length === 0) {
      try {
        // Use the backend endpoint with the corrected query
        const exclude = encodeURIComponent(getKotilingaCategorySlugsCsv());
        const range = buildRangeParams();
        const res = await get(`/ldb/reports/pledges/by-product/${pid}/variations?exclude_parent_category_slugs=${exclude}&status=pending${range}`);
        const rows = Array.isArray(res?.data) ? res.data : [];
        
        // Transform to match expected format
        const arr = rows.map(r => ({
          variation_id: r.variation_id || 0,
          variation_name: r.variation_name || r.name || '',
          pledge_total: Number(r.pledge_total ?? r.sum ?? r.total ?? 0),
          paid_total: Number(r.paid_total || 0),
          balance_total: Number(r.balance_total || 0),
          donors_count: Number(r.count || 0)
        }))
        .filter(x => Number(x.pledge_total || 0) > 0)
        .sort((a, b) => Number(b.pledge_total || 0) - Number(a.pledge_total || 0));
        
        setPledgeVarMap(prev => ({ ...prev, [pid]: arr }));
      } catch (e) {
        console.error('[Dashboard] Failed to load variation details:', e);
        setPledgeVarMap(prev => ({ ...prev, [pid]: [] }));
      }
    }
    setPledgeExpanded(prev => ({ ...prev, [pid]: true }));
  };

  // Measure pie containers to give explicit width/height to PieChart (ResponsiveContainer can under-measure in Grid layouts)
  React.useEffect(() => {
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width);
        if (entry.target === prodPieRef.current) setProdW(w);
        if (entry.target === catPieRef.current) setCatW(w);
        if (entry.target === prodBarRef.current) setProdBarW(w);
      }
    });
    if (prodPieRef.current) ro.observe(prodPieRef.current);
    if (catPieRef.current) ro.observe(catPieRef.current);
    if (prodBarRef.current) ro.observe(prodBarRef.current);
    return () => ro.disconnect();
  }, []);

  const COLORS = ['#1976d2','#9c27b0','#2e7d32','#ed6c02','#d32f2f','#455a64','#6d4c41','#283593','#00897b','#7b1fa2'];
  const moneyTick = (v) => {
    const n = Number(v || 0);
    if (n >= 1_000_000) return `$${Math.round(n/1_000_000)}M`;
    if (n >= 1_000) return `$${Math.round(n/1000)}k`;
    return Number.isFinite(n) ? `$${Math.round(n).toLocaleString()}` : '$0';
  };
  const openPledgeDlg = async () => {
    setPledgeDlgOpen(true);
    try {
      const res = await get('/ldb/reports/pledges/list', { status: 'all', page: 1, per_page: 25 });
      setPledgeDlgData({ data: res?.data || [], total: res?.meta?.total || 0, page: 1, per_page: 25 });
    } catch (e) {}
  };
  const closePledgeDlg = () => setPledgeDlgOpen(false);

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Temple Dashboard</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', mr: 1 }}>View:</Typography>
          <Box>
            <Link to="/dashboard/temple" style={{ textDecoration: 'none', marginRight: 8 }}>
              <button style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: location.pathname === '/dashboard/temple' || location.pathname === '/dashboard' ? '1px solid transparent' : '1px solid #ccc',
                background: location.pathname === '/dashboard/temple' || location.pathname === '/dashboard' ? '#1976d2' : 'white',
                color: location.pathname === '/dashboard/temple' || location.pathname === '/dashboard' ? 'white' : '#1976d2',
                cursor: 'pointer'
              }}>Temple</button>
            </Link>
            <Link to="/dashboard/kotilinga" style={{ textDecoration: 'none' }}>
              <button style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: location.pathname === '/dashboard/kotilinga' ? '1px solid transparent' : '1px solid #ccc',
                background: location.pathname === '/dashboard/kotilinga' ? '#1976d2' : 'white',
                color: location.pathname === '/dashboard/kotilinga' ? 'white' : '#1976d2',
                cursor: 'pointer'
              }}>Kotilinga</button>
            </Link>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <button onClick={()=>setRangeType('all')} style={{ padding: '6px 10px', borderRadius: 8, border: rangeType==='all'?'1px solid transparent':'1px solid #ccc', background: rangeType==='all'?'#283593':'white', color: rangeType==='all'?'white':'#283593' }}>All</button>
        <button onClick={()=>setRangeType('last12')} style={{ padding: '6px 10px', borderRadius: 8, border: rangeType==='last12'?'1px solid transparent':'1px solid #ccc', background: rangeType==='last12'?'#283593':'white', color: rangeType==='last12'?'white':'#283593' }}>Last 12 mon</button>
        <button onClick={()=>setRangeType('lastYear')} style={{ padding: '6px 10px', borderRadius: 8, border: rangeType==='lastYear'?'1px solid transparent':'1px solid #ccc', background: rangeType==='lastYear'?'#283593':'white', color: rangeType==='lastYear'?'white':'#283593' }}>Last year</button>
        <button onClick={()=>setRangeType('ytd')} style={{ padding: '6px 10px', borderRadius: 8, border: rangeType==='ytd'?'1px solid transparent':'1px solid #ccc', background: rangeType==='ytd'?'#283593':'white', color: rangeType==='ytd'?'white':'#283593' }}>YTD</button>
        <button onClick={()=>setRangeType('custom')} style={{ padding: '6px 10px', borderRadius: 8, border: rangeType==='custom'?'1px solid transparent':'1px solid #ccc', background: rangeType==='custom'?'#283593':'white', color: rangeType==='custom'?'white':'#283593' }}>Custom</button>
        <select value={rangeType==='year'?rangeYear:''} onChange={(e)=>{ setRangeType('year'); setRangeYear(Number(e.target.value)); }} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ccc' }}>
          {(()=>{ const thisY = new Date().getFullYear(); const years = []; for(let y=thisY; y>=thisY-6; y--) years.push(y); return years; })().map(y=> (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        {rangeType==='custom' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input type="date" value={customStart} onChange={(e)=>setCustomStart(e.target.value)} />
            <input type="date" value={customEnd} onChange={(e)=>setCustomEnd(e.target.value)} />
          </Box>
        )}
      </Box>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <Grid container spacing={0} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6} sx={{ flexBasis: { md: 'calc(50% - 2.5px)' }, maxWidth: { md: 'calc(50% - 2.5px)' }, minWidth: 0, mr: { md: '5px' } }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: '#eef3ff', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Donations Summary</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><Kpi label="Total Donations" value={formatAmount(summary?.total_donations)} /></Grid>
              <Grid item xs={12} sm={6}><Kpi label="Orders" value={summary?.order_count ?? '--'} /></Grid>
              <Grid item xs={12} sm={6}><Kpi label="Avg Donation" value={formatAmount(summary?.avg_donation)} /></Grid>
              <Grid item xs={12} sm={6}><Kpi label="Donors" value={summary?.donors_count ?? '--'} /></Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} sx={{ flexBasis: { md: 'calc(50% - 2.5px)' }, maxWidth: { md: 'calc(50% - 2.5px)' }, minWidth: 0 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: '#eefcf0', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Pledge Summary</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><Box onClick={openPledgeDlg} sx={{ cursor: 'pointer' }}><Kpi label="Pledges" value={pledges?.total_count ?? '--'} /></Box></Grid>
              <Grid item xs={12} sm={6}><Box onClick={openPledgeDlg} sx={{ cursor: 'pointer' }}><Kpi label="Total" value={formatAmount(pledges?.total_amount)} /></Box></Grid>
              <Grid item xs={12} sm={6}><Box onClick={openPledgeDlg} sx={{ cursor: 'pointer' }}><Kpi label="Paid" value={formatAmount(pledges?.paid_amount)} /></Box></Grid>
              <Grid item xs={12} sm={6}><Box onClick={openPledgeDlg} sx={{ cursor: 'pointer' }}><Kpi label="Balance" value={formatAmount(pledges?.balance_amount)} /></Box></Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={0} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6} sx={{ flexBasis: { md: 'calc(50% - 2.5px)' }, maxWidth: { md: 'calc(50% - 2.5px)' }, minWidth: 0, mr: { md: '5px' } }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, height: 420, overflow: 'hidden', width: '100%', minWidth: 0, bgcolor: '#f7faff', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Donations - Monthly Trend</Typography>
            <Box sx={{ height: 360, minWidth: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={seriesMonth.slice(-12).map(d => ({ bucket: new Date(d.bucket).toISOString().slice(0,10), amount: Number(d.amount || 0) }))} margin={{ top: 8, right: 16, bottom: 8, left: 16 }} barSize={18}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 12 }} tickMargin={6} minTickGap={24} tickFormatter={(v)=>{
                    try { const d = new Date(v); return d.toLocaleString(undefined,{ month:'short', year:'2-digit' }); } catch { return v; }
                  }} />
                  <YAxis tickFormatter={moneyTick} width={60} allowDecimals={false} domain={[0, 'auto']} tickMargin={4} />
                  <Tooltip formatter={(v)=>formatAmount(v)} labelFormatter={(l)=>`Bucket: ${l}`} />
                  <Bar dataKey="amount" fill="#1976d2" radius={[4,4,0,0]}>
                    <LabelList dataKey="amount" position="top" formatter={(v) => formatAmount(v)} style={{ fontSize: 10 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} sx={{ flexBasis: { md: 'calc(50% - 2.5px)' }, maxWidth: { md: 'calc(50% - 2.5px)' }, minWidth: 0 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, height: 420, overflow: 'hidden', width: '100%', minWidth: 0, bgcolor: '#f7faff', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Donations - Yearly</Typography>
            <Box sx={{ height: 360, minWidth: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={seriesYear.map(d => ({ year: new Date(d.bucket).getUTCFullYear(), amount: Number(d.amount || 0) }))} margin={{ top: 8, right: 16, bottom: 8, left: 16 }} barSize={22}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis tickFormatter={moneyTick} width={60} />
                  <Tooltip formatter={(v)=>formatAmount(v)} />
                  <Bar dataKey="amount" fill="#9c27b0" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={12} sx={{ flexBasis: { md: '100%' }, maxWidth: { md: '100%' }, minWidth: 0 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: '#f7faff', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Top 10 Products (Donations)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Autocomplete
                  size="small"
                  options={prodSplit || []}
                  getOptionLabel={(option) => option.product_name || `Product #${option.product_id}`}
                  value={selectedProductFilter}
                  onChange={(event, newValue) => {
                    setSelectedProductFilter(newValue);
                    setProdPage(1); // Reset to first page when filtering
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search product..."
                      sx={{ width: 250 }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.product_id === value?.product_id}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography variant="body2">{option.product_name || `Product #${option.product_id}`}</Typography>
                    </Box>
                  )}
                />
                <button onClick={()=>setRangeType('all')} style={{ padding: '6px 10px', borderRadius: 8, border: rangeType==='all'?'1px solid transparent':'1px solid #ccc', background: rangeType==='all'?'#283593':'white', color: rangeType==='all'?'white':'#283593' }}>All</button>
                <button onClick={()=>setRangeType('last12')} style={{ padding: '6px 10px', borderRadius: 8, border: rangeType==='last12'?'1px solid transparent':'1px solid #ccc', background: rangeType==='last12'?'#283593':'white', color: rangeType==='last12'?'white':'#283593' }}>Last 12 mon</button>
                <button onClick={()=>setRangeType('lastYear')} style={{ padding: '6px 10px', borderRadius: 8, border: rangeType==='lastYear'?'1px solid transparent':'1px solid #ccc', background: rangeType==='lastYear'?'#283593':'white', color: rangeType==='lastYear'?'white':'#283593' }}>Last year</button>
                <button onClick={()=>setRangeType('ytd')} style={{ padding: '6px 10px', borderRadius: 8, border: rangeType==='ytd'?'1px solid transparent':'1px solid #ccc', background: rangeType==='ytd'?'#283593':'white', color: rangeType==='ytd'?'white':'#283593' }}>YTD</button>
                <button onClick={()=>setRangeType('custom')} style={{ padding: '6px 10px', borderRadius: 8, border: rangeType==='custom'?'1px solid transparent':'1px solid #ccc', background: rangeType==='custom'?'#283593':'white', color: rangeType==='custom'?'white':'#283593' }}>Custom</button>
                <select value={rangeType==='year'?rangeYear:''} onChange={(e)=>{ setRangeType('year'); setRangeYear(Number(e.target.value)); }} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ccc' }}>
                  <option value="" disabled>Year</option>
                  {(()=>{ const thisY = new Date().getFullYear(); const years = []; for(let y=thisY; y>=thisY-6; y--) years.push(y); return years; })().map(y=> (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                {rangeType==='custom' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input type="date" value={customStart} onChange={(e)=>setCustomStart(e.target.value)} style={{ padding: '4px', borderRadius: 6, border: '1px solid #ccc' }} />
                    <input type="date" value={customEnd} onChange={(e)=>setCustomEnd(e.target.value)} style={{ padding: '4px', borderRadius: 6, border: '1px solid #ccc' }} />
                  </Box>
                )}
              </Box>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {prodSplit.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No data</Typography>
            ) : (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                {(() => {
                  const sorted = [...prodSplit].sort((a,b)=>Number(b.amount||0)-Number(a.amount||0));
                  const total = sorted.length;
                  const totalPages = Math.max(1, Math.ceil(total / prodPerPage));
                  const page = Math.min(prodPage, totalPages);
                  const startIdx = (page - 1) * prodPerPage;
                  const rows = sorted.slice(startIdx, startIdx + prodPerPage);
                  return (
                    <>
                      {(total > prodPerPage) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2">Page {page} of {totalPages}</Typography>
                          <button disabled={page <= 1} onClick={()=>setProdPage(page-1)} style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc', background: 'white', color: '#1976d2', cursor: 'pointer' }}>Prev</button>
                          {(() => {
                            const windowSize = 7;
                            let start = Math.max(1, page - Math.floor(windowSize/2));
                            let end = Math.min(totalPages, start + windowSize - 1);
                            start = Math.max(1, end - windowSize + 1);
                            const pages = [];
                            for (let pg = start; pg <= end; pg++) pages.push(pg);
                            return pages.map(pg => (
                              <button key={`prod-top-${pg}`} onClick={()=>setProdPage(pg)} disabled={pg===page} style={{ padding: '2px 8px', borderRadius: 4, border: pg===page ? '1px solid transparent' : '1px solid #ccc', background: pg===page ? '#1976d2' : 'white', color: pg===page ? 'white' : '#1976d2', cursor: 'pointer' }}>{pg}</button>
                            ));
                          })()}
                          <button disabled={page >= totalPages} onClick={()=>setProdPage(page+1)} style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc', background: 'white', color: '#1976d2', cursor: 'pointer' }}>Next</button>
                        </Box>
                      )}

                      <Box sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 140px 120px', bgcolor: 'action.hover' }}>
                        <Typography variant="caption" sx={{ p: 1, fontWeight: 700 }}></Typography>
                        <Typography variant="caption" sx={{ p: 1, fontWeight: 700 }}>Product</Typography>
                        <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Orders</Typography>
                        <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Amount</Typography>
                      </Box>

                      <Box>
                        {(selectedProductFilter ? rows.filter(p => p.product_id === selectedProductFilter.product_id) : rows).map((p,i)=> {
                          const noVariations = Array.isArray(variationsMap[p.product_id]) && variationsMap[p.product_id].length === 0;
                          return (
                          <React.Fragment key={`${p.product_id}-${i}`}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 140px 120px', borderTop: '1px solid', borderColor: 'divider', bgcolor: i % 2 === 0 ? 'transparent' : 'action.hover' }}>
                              <Box sx={{ p: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <IconButton size="small" disabled={noVariations} onClick={()=>toggleExpandProduct(p)} aria-label="Expand" sx={{ border: '1px solid', borderColor: 'divider' }}>
                                  {expanded[p.product_id] ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                </IconButton>
                              </Box>
                              <Typography 
                                variant="body2" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDonationsByCustomerDialog(p, null);
                                }}
                                sx={{ 
                                  p: 0.25, 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis', 
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                  '&:hover': { 
                                    color: 'primary.main',
                                    textDecoration: 'underline'
                                  }
                                }} 
                                title={p.product_name || `#${p.product_id}`}
                              >
                                {p.product_name || `#${p.product_id}`}
                              </Typography>
                              <Typography variant="body2" sx={{ p: 0.25, textAlign: 'right' }}>{Number(p.orders||0).toLocaleString()}</Typography>
                              <Typography variant="body2" sx={{ p: 0.25, textAlign: 'right' }}>{formatAmount(p.amount)}</Typography>
                            </Box>
                            {expanded[p.product_id] && (variationsMap[p.product_id] || []).map((v,vi)=> (
                              <Box key={`${p.product_id}-var-${v.variation_id}-${vi}`} onClick={()=>openDonationsByCustomerDialog(p, v)} sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 140px 120px', borderTop: '1px dashed', borderColor: 'divider', bgcolor: i % 2 === 0 ? 'transparent' : 'action.hover', color: 'secondary.main', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' }, '&:hover *': { color: 'text.secondary' } }}>
                                <Box></Box>
                                <Typography variant="body2" sx={{ p: 0.25, pl: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.variation_name || `Variation #${v.variation_id}`}</Typography>
                                <Typography variant="body2" sx={{ p: 0.25, textAlign: 'right' }}>{Number(v.orders||0).toLocaleString()}</Typography>
                                <Typography variant="body2" sx={{ p: 0.25, textAlign: 'right' }}>{formatAmount(v.amount)}</Typography>
                              </Box>
                            ))}
                          </React.Fragment>
                          );
                        })}
                      </Box>

                      {(total > prodPerPage) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2">Page {page} of {totalPages}</Typography>
                          <button disabled={page <= 1} onClick={()=>setProdPage(page-1)} style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc', background: 'white', color: '#1976d2', cursor: 'pointer' }}>Prev</button>
                          {(() => {
                            const windowSize = 7;
                            let start = Math.max(1, page - Math.floor(windowSize/2));
                            let end = Math.min(totalPages, start + windowSize - 1);
                            start = Math.max(1, end - windowSize + 1);
                            const pages = [];
                            for (let pg = start; pg <= end; pg++) pages.push(pg);
                            return pages.map(pg => (
                              <button key={`prod-bot-${pg}`} onClick={()=>setProdPage(pg)} disabled={pg===page} style={{ padding: '2px 8px', borderRadius: 4, border: pg===page ? '1px solid transparent' : '1px solid #ccc', background: pg===page ? '#1976d2' : 'white', color: pg===page ? 'white' : '#1976d2', cursor: 'pointer' }}>{pg}</button>
                            ));
                          })()}
                          <button disabled={page >= totalPages} onClick={()=>setProdPage(page+1)} style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc', background: 'white', color: '#1976d2', cursor: 'pointer' }}>Next</button>
                        </Box>
                      )}
                    </>
                  );
                })()}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={12} sx={{ flexBasis: { md: '100%' }, maxWidth: { md: '100%' }, minWidth: 0 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: '#f7faff', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Pending Pledges</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Autocomplete
                  size="small"
                  options={pledgesByProduct || []}
                  getOptionLabel={(option) => option.product_name || `Product #${option.product_id}`}
                  value={selectedPledgeProductFilter}
                  onChange={(event, newValue) => {
                    setSelectedPledgeProductFilter(newValue);
                    setPledgesPage(1); // Reset to first page when filtering
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search product..."
                      sx={{ width: 250 }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.product_id === value?.product_id}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography variant="body2">{option.product_name || `Product #${option.product_id}`}</Typography>
                    </Box>
                  )}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadCSV}
                  disabled={loading || pledgesByProduct.length === 0}
                >
                  Download CSV
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {(() => {
              const total = pledgesByProduct.length;
              const totalPages = Math.max(1, Math.ceil(total / pledgesPerPage));
              const page = Math.min(pledgesPage, totalPages);
              const startIdx = (page - 1) * pledgesPerPage;
              const rows = pledgesByProduct.slice(startIdx, startIdx + pledgesPerPage);
              
              // Calculate grand totals from all products (not just current page)
              const grandTotals = pledgesByProduct.reduce((acc, p) => ({
                donors_count: acc.donors_count + Number(p.donors_count || 0),
                pledge_total: acc.pledge_total + Number(p.pledge_total || 0),
                paid_total: acc.paid_total + Number(p.paid_total || 0),
                balance_total: acc.balance_total + Number(p.balance_total || 0)
              }), { donors_count: 0, pledge_total: 0, paid_total: 0, balance_total: 0 });
              
              return (
                <>
                  {(total > pledgesPerPage) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                      <Typography variant="body2">Page {page} of {totalPages}</Typography>
                      <button disabled={page <= 1} onClick={()=>setPledgesPage(page-1)} style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc', background: 'white', color: '#1976d2', cursor: 'pointer' }}>Prev</button>
                      {(() => {
                        const windowSize = 7;
                        let start = Math.max(1, page - Math.floor(windowSize/2));
                        let end = Math.min(totalPages, start + windowSize - 1);
                        start = Math.max(1, end - windowSize + 1);
                        const btns = [];
                        for (let pg = start; pg <= end; pg++) btns.push(pg);
                        return btns.map(pg => (
                          <button key={`pledges-top-${pg}`} onClick={()=>setPledgesPage(pg)} disabled={pg===page} style={{ padding: '2px 8px', borderRadius: 4, border: pg===page ? '1px solid transparent' : '1px solid #ccc', background: pg===page ? '#1976d2' : 'white', color: pg===page ? 'white' : '#1976d2', cursor: 'pointer' }}>{pg}</button>
                        ));
                      })()}
                      <button disabled={page >= totalPages} onClick={()=>setPledgesPage(page+1)} style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc', background: 'white', color: '#1976d2', cursor: 'pointer' }}>Next</button>
                    </Box>
                  )}

                  <Box sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 120px 140px 140px 140px', bgcolor: 'action.hover' }}>
                    <Typography variant="caption" sx={{ p: 1, fontWeight: 700 }}></Typography>
                    <Typography variant="caption" sx={{ p: 1, fontWeight: 700 }}>Product</Typography>
                    <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Donors</Typography>
                    <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Pledge</Typography>
                    <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Paid</Typography>
                    <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Balance</Typography>
                  </Box>
                    <Box>
                      {(selectedPledgeProductFilter ? rows.filter(p => p.product_id === selectedPledgeProductFilter.product_id) : rows).map((p,i)=> (
                        <React.Fragment key={`${p.product_id}-${i}`}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 120px 140px 140px 140px', borderTop: '1px solid', borderColor: 'divider', bgcolor: i % 2 === 0 ? 'transparent' : 'action.hover' }}>
                            <Box sx={{ p: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <IconButton size="small" onClick={()=>toggleExpandPledgeProduct(p)} aria-label="Expand" sx={{ border: '1px solid', borderColor: 'divider' }}>
                                {pledgeExpanded[p.product_id] ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                              </IconButton>
                            </Box>
                            <Typography 
                              variant="body2" 
                              onClick={()=>toggleExpandPledgeProduct(p)}
                              sx={{ 
                                p: 0.25, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                                '&:hover': { 
                                  color: 'primary.main',
                                  textDecoration: 'underline'
                                }
                              }} 
                              title={p.product_name || `#${p.product_id}`}
                            >
                              {p.product_name || `#${p.product_id}`}
                            </Typography>
                            <Typography variant="body2" sx={{ p: 0.25, textAlign: 'right' }}>{Number(p.donors_count||0).toLocaleString()}</Typography>
                            <Typography variant="body2" sx={{ p: 0.25, textAlign: 'right' }}>{formatAmount(p.pledge_total)}</Typography>
                            <Typography variant="body2" sx={{ p: 0.25, textAlign: 'right' }}>{formatAmount(p.paid_total)}</Typography>
                            <Typography variant="body2" sx={{ p: 0.25, textAlign: 'right' }}>{formatAmount(p.balance_total)}</Typography>
                          </Box>
                          {pledgeExpanded[p.product_id] && (pledgeVarMap[p.product_id] || []).map((v,vi)=> (
                            <Box key={`${p.product_id}-plvar-${v.variation_id}-${vi}`} onClick={()=>openPledgesByDevoteeDialog({ product_id: p.product_id, product_name: p.product_name }, v)} sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 120px 140px 140px 140px', borderTop: '1px dashed', borderColor: 'divider', bgcolor: i % 2 === 0 ? 'transparent' : 'action.hover', color: 'secondary.main', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' }, '&:hover *': { color: 'text.secondary' } }}>
                              <Box></Box>
                              <Typography variant="body2" sx={{ p: 0.25, pl: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v.variation_name || `Variation #${v.variation_id}`}>{v.variation_name || `Variation #${v.variation_id}`}</Typography>
                              <Typography variant="body2" sx={{ p: 0.25, textAlign: 'right' }}>{Number(v.donors_count||0).toLocaleString()}</Typography>
                              <Typography variant="body2" sx={{ p: 0.25, textAlign: 'right' }}>{formatAmount(v.pledge_total)}</Typography>
                              <Typography variant="body2" sx={{ p: 0.25, textAlign: 'right' }}>{formatAmount(v.paid_total)}</Typography>
                              <Typography variant="body2" sx={{ p: 0.25, textAlign: 'right' }}>{formatAmount(v.balance_total)}</Typography>
                            </Box>
                          ))}
                        </React.Fragment>
                      ))}
                      
                      {/* Grand Totals Row */}
                      {pledgesByProduct.length > 0 && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 120px 140px 140px 140px', borderTop: '2px solid', borderColor: 'primary.main', bgcolor: 'primary.light', '& *': { color: 'primary.contrastText', fontWeight: 700 } }}>
                          <Box></Box>
                          <Typography variant="body2" sx={{ p: 1 }}>Grand Total</Typography>
                          <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{Number(grandTotals.donors_count).toLocaleString()}</Typography>
                          <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(grandTotals.pledge_total)}</Typography>
                          <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(grandTotals.paid_total)}</Typography>
                          <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(grandTotals.balance_total)}</Typography>
                        </Box>
                      )}

                      {rows.length === 0 && (
                        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                          {loading ? (
                            <>
                              <CircularProgress size={24} />
                              <Typography variant="body2" color="text.secondary">Loading pledge data...</Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">No data</Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                  {(total > pledgesPerPage) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                      <Typography variant="body2">Page {page} of {totalPages}</Typography>
                      <button disabled={page <= 1} onClick={()=>setPledgesPage(page-1)} style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc', background: 'white', color: '#1976d2', cursor: 'pointer' }}>Prev</button>
                      {(() => {
                        const windowSize = 7;
                        let start = Math.max(1, page - Math.floor(windowSize/2));
                        let end = Math.min(totalPages, start + windowSize - 1);
                        start = Math.max(1, end - windowSize + 1);
                        const btns = [];
                        for (let pg = start; pg <= end; pg++) btns.push(pg);
                        return btns.map(pg => (
                          <button key={`pledges-bot-${pg}`} onClick={()=>setPledgesPage(pg)} disabled={pg===page} style={{ padding: '2px 8px', borderRadius: 4, border: pg===page ? '1px solid transparent' : '1px solid #ccc', background: pg===page ? '#1976d2' : 'white', color: pg===page ? 'white' : '#1976d2', cursor: 'pointer' }}>{pg}</button>
                        ));
                      })()}
                      <button disabled={page >= totalPages} onClick={()=>setPledgesPage(page+1)} style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc', background: 'white', color: '#1976d2', cursor: 'pointer' }}>Next</button>
                    </Box>
                  )}
                </>
              );
            })()}
          </Paper>
        </Grid>
      </Grid>

      <PendingPledgesDialog
        open={pledgeDevDlgOpen}
        onClose={closePledgesByDevoteeDialog}
        product={pledgeDevDlgProduct}
        buildRangeParams={buildRangeParams}
      />

      <DonationsByCustomerDialog
        open={donationsDlgOpen}
        onClose={closeDonationsByCustomerDialog}
        product={donationsDlgProduct}
        buildRangeParams={buildRangeParams}
      />

      <OrdersDialog
        open={orderDlgOpen}
        onClose={closeOrdersDialog}
        product={orderDlgProduct}
        buildProdRangeParams={buildRangeParams}
        formatAmount={formatAmount}
      />
    </PageLayout>
  );
}
